import { Component, inject } from '@angular/core';
import { PageComponent, FormHandlingComponent, Page, FormInput, Redirection } from '@tytapp/common';
import { UserService } from '@tytapp/user';
import { Validated, NonEmptyValidator } from 'validation';
import { NoticeService } from '@tytaccounts/common';
import { ApiCancellationReason, UsersApi, ApiRetentionOfferSet, ApiMembership, ApiUser, MembershipApi, ApiMembershipProduct } from '@tytaccounts/api';
import { CancelMembershipRetentionComponent } from '../cancel-membership-retention/cancel-membership-retention.component';
import { environment } from 'environments/environment';

@Page('cancelMembership')
@Component({
  selector: 'app-cancel-membership',
  templateUrl: './cancel-membership.component.html',
  styleUrls: ['./cancel-membership.component.scss']
})
export class CancelMembershipComponent extends PageComponent implements FormHandlingComponent {
  private userService = inject(UserService);
  private notices = inject(NoticeService);
  private redirection = inject(Redirection);
  private usersApi = inject(UsersApi);
  private membershipApi = inject(MembershipApi);

  @Validated({ class: NonEmptyValidator, field: 'reason' })
  @FormInput()
  reason: string = '';

  @Validated({ field: 'feedback' })
  @FormInput()
  feedback: string = "";

  @FormInput()
  immediateCancel: boolean = false;
  
  checkboxCancel: boolean;
  user: ApiUser;
  membership: ApiMembership;
  membershipProduct: ApiMembershipProduct;
  reasonList: ApiCancellationReason[];
  retentionOfferSet: ApiRetentionOfferSet;

  private shuffle(array : any[]) {
    for (let i = array.length - 1; i > 0; i--) {
      let j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }

    return array;
  }

  private async testOpenRetention() {
    if (!environment.showDevTools)
      return;

    this.reason = 'too_busy';
    this.retentionOfferSet = await this.getRetentionOffer();

    if(this.retentionOfferSet != null) {
      this.shell.showModalDialog(CancelMembershipRetentionComponent, this.retentionOfferSet, this);
    } else {
      this.allowCancellation();
    }
  }

  init() {
    //this.testOpenRetention();

    this.checkboxCancel = environment.settings.allowImmediateCancel;
    this.subscribe(this.userService.userChanged, async user => {
      this.user = user;
      if (!this.user) {
        this.userService.goToLogin();
        return;
      }

      if (!this.user.membership) {
        this.redirection.go('/settings/memberships');
        return;
      }

      this.membership = user.membership;
      if (this.membership) {
        if (!this.reasonList) {
          this.reasonList = await this.membershipApi.getCancellationReasons().toPromise();
          this.reasonList = this.shuffle(this.reasonList);
        }
      }
    });
  }

  async submit() {

    if (typeof window === 'undefined') {
      // Server side, can't show dialogs.
      
      let date = new Date(this.membership.renews_on);

      console.log(this.reason);
      return;

      // grab membership good_to date before cancelling
      await this.usersApi.cancelMembership({
        reason: this.reason ,
        reason_details: this.feedback,
        at_period_end: !this.immediateCancel
      }).toPromise();
      this.user.membership.active = false;
      let formatDate = (date.getMonth() + 1) + '/' + date.getDate() + '/' + date.getFullYear();
      let message = 'Your membership has been cancelled.  You can continue to access your membership benefits through ' + formatDate;
      this.notices.post(message, 'info');
      this.redirection.go('/settings/memberships');
      return;
    }

    this.retentionOfferSet = await this.getRetentionOffer();

    if(this.retentionOfferSet != null) {
      this.shell.showModalDialog(CancelMembershipRetentionComponent, this.retentionOfferSet, this);
    } else {
      this.allowCancellation();
    }
  }

  async allowCancellation() {
    // If we didn't showed a retention offer we show a cancellation confirmation prompt
    if(this.retentionOfferSet) {
      this.cancelMembership();
      return;
    }

    let message: string;
    if (this.reason == "technical") {
      message = `
        <div>
          <h4 class='title'>Did You Know?</h4>
          <div>
            Many technical issues can be resolved by visiting the <a href="https://help.tyt.com/">Help Center</a>
            or contacting TYT Member Services at support@tytnetwork.com.
            We're here to help!
          </div>
          
          Are you sure you want to cancel your TYT membership?
          You will remain entitled until your normal renewal date.

        </div>
      `;

    } else {
      message = `
        Are you sure you want to cancel your TYT membership?
        You will remain entitled until your normal renewal date.
      `;
    }

    if (!await this.shell.confirm('Are you sure?', message, { confirmText: 'Yes, cancel my membership', cancelText: 'No, keep membership' })) {
      this.router.navigateByUrl('/settings/membership-accounts');
      return;
    }

    await this.cancelMembership();
  }

  async cancelMembership() {
    try {
        let date = new Date(this.membership.renews_on);
        // Call API to cancel membership
        await this.usersApi.cancelMembership({
            reason: this.reason || 'none', 
            reason_details: this.feedback, 
            at_period_end: !this.immediateCancel
        }).toPromise();

        // Format the renewal date for the message
        let formatDate = (date.getMonth() + 1) + '/' + date.getDate() + '/' + date.getFullYear();
        let message = 'Your membership has been cancelled. You can continue to access your membership benefits through ' + formatDate;
        this.notices.post(message, 'info');
        let product = this.membership.product;
        // Send analytics data for membership cancellation
        if (typeof window !== 'undefined') {
            await this.userService.sendMembershipCancelAnalytics(
                this.reason || 'none',      // Cancellation reason
                product.price,      // Membership price
                product.period,      // Billing period (e.g., 'monthly' or 'annually')
                product.product_sku  //product SKU   
            );
        }
        // Redirect to cancellation success page
        this.redirection.go('/membership/cancel/success');
    } catch (e) {
        console.error("Error: Unable to cancel membership:");
        console.error(e);
    }
}

  toPrevious() {
    this.redirection.go('/settings/memberships');
  }

  async getRetentionOffer(): Promise<ApiRetentionOfferSet> {
    return new Promise<ApiRetentionOfferSet>((resolve, reject) => {
      this.usersApi.getRetentionOffer(this.reason).toPromise().then((offer) => {
        resolve(offer);
      }).catch(() => {
        resolve(null);
      });
    });
  }
}